// import React, { useMemo } from "react";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate and Link
import { generateGuid, formatDate } from "@utils/utils";
import ReactTimeAgo from "react-time-ago";
import ModalConfirm from "@components/misc/ModalConfirm";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { fetchIntegrations, deleteIntegrationAsync } from "@redux/slices/integrationSlice";
import { parseError } from "../../utils/utils";
import Tabs from "@components/misc/Tabs";
import { selectUserInfo } from "@redux/slices/authSlice"; // Import user info selector
import { deletePipeline } from "@services/httpservice";
import ScrollContainer from "@components/misc/ScrollContainer";
import { ArrowLeft } from "lucide-react";

// https://www.creative-tim.com/twcomponents/component/table-with-filter-and-search

const Listing = () => {
  const navigate = useNavigate(); // Get the navigate function

  const dispatch = useDispatch();
  const integrations = useSelector((state) => state.integration.integrations);
  const integrationsCount = useSelector(
    (state) => state.integration.integrationsCount
  );
  const status = useSelector((state) => state.integration.status);
  const error = useSelector((state) => state.integration.error);
  const userInfo = useSelector(selectUserInfo); // Get user info for role-based access control

  const [listFilterStatus, setListFilterStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [clientPage, setClientPage] = useState(1);
  const rowsPerPage = 8;
  const [selectedItemToDelete, setSelectedItemeToDelete] = useState(null);
  const [selectedPipelineToDelete, setSelectedPipelineToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // eslint-disable-next-line no-unused-vars
  const showPagination = false;
  const pageSize = 1000;

  const filteredIntegrations = useMemo(() => {
    if (!searchQuery) return integrations;
    const query = searchQuery.toLowerCase();
    const isPartialMatch = (queryStr, target) => {
      if (!target) return false;
      const t = target.toLowerCase();
      if (t.includes(queryStr)) return true;
      const words = queryStr.split(/\s+/).filter(Boolean);
      if (words.length > 0) {
        const matchedWords = words.filter(word => t.includes(word));
        if (matchedWords.length / words.length >= 0.5) return true;
      }
      let qIndex = 0, matchCount = 0;
      for (let i = 0; i < t.length; i++) {
        if (qIndex < queryStr.length && t[i] === queryStr[qIndex]) { matchCount++; qIndex++; }
      }
      return (queryStr.length > 0 && matchCount / queryStr.length >= 0.5);
    };
    return integrations.filter((item) =>
      isPartialMatch(query, item.name) || isPartialMatch(query, item.description)
    );
  }, [integrations, searchQuery]);

  // Sort integrations so that legs belonging to the same multi-route workflow
  // appear adjacent, ordered by leg_order. Overall order is by first-item createdAt descending.
  const displayRows = useMemo(() => {
    // Separate pipeline-owned from standalone
    const pipelineGroups = {};
    const standalone = [];
    filteredIntegrations.forEach((item) => {
      if (item.pipeline) {
        if (!pipelineGroups[item.pipeline]) pipelineGroups[item.pipeline] = [];
        pipelineGroups[item.pipeline].push(item);
      } else {
        standalone.push(item);
      }
    });
    // Sort legs within each pipeline by leg_order
    Object.values(pipelineGroups).forEach((legs) =>
      legs.sort((a, b) => (a.leg_order ?? 0) - (b.leg_order ?? 0))
    );

    // Build display rows — one row per pipeline, one row per standalone integration
    const allRows = [
      ...standalone.map((item) => ({
        type: "standalone",
        key: item.integration_id,
        item,
        date: item.createdAt,
      })),
      ...Object.entries(pipelineGroups).map(([pipelineId, legs]) => ({
        type: "pipeline",
        key: `pipeline-${pipelineId}`,
        pipelineId,
        legs,
        date: legs[0]?.createdAt,
        // Aggregate status: if any failed → Failed, all running → Running, else first leg status
        status: legs.some((l) => l.status === "Failed")
          ? "Failed"
          : legs.every((l) => l.status === "Running")
            ? "Running"
            : legs[0]?.status,
        name: legs[0]?.pipeline_name || legs[0]?.name || `Integration (${legs.length})`,
        pipeline_type: legs[0]?.pipeline_type,
        agent_session_id: legs[0]?.agent_session_id || null,
        createdBy: legs[0]?.createdBy,
        createdAt: legs[0]?.createdAt,
        description: legs[0]?.pipeline_type === "sequential"
          ? legs.map(l => l.sourceDetails?.platform || l.destinationDetails?.destinationType || "Step").join(" → ")
          : legs[0]?.pipeline_type === "fan_out"
          ? `Sends data to ${legs.length} destinations`
          : `Collects data from ${legs.length} sources`,
        lastExecutedAt: legs.reduce((latest, l) => {
          if (!l.lastExecutedAt) return latest;
          if (!latest) return l.lastExecutedAt;
          return new Date(l.lastExecutedAt) > new Date(latest) ? l.lastExecutedAt : latest;
        }, null),
      })),
    ];
    allRows.sort((a, b) => new Date(b.date) - new Date(a.date));
    return allRows;
  }, [filteredIntegrations]);

  useEffect(() => {
    dispatch(
      fetchIntegrations({
        page: currentPage,
        page_size: pageSize,
        status: listFilterStatus,
      })
    );
  }, [dispatch, currentPage, pageSize, listFilterStatus]);

  // useEffect(() => {
  //   dispatch(fetchIntegrations(currentPage, pageSize, listFilterStatus));
  // }, [listFilterStatus])

  const totalPages = () => {
    return Math.ceil(displayRows.length / rowsPerPage);
  };

  const pagedRows = useMemo(() => {
    const start = (clientPage - 1) * rowsPerPage;
    return displayRows.slice(start, start + rowsPerPage);
  }, [displayRows, clientPage, rowsPerPage]);

  const handleRefresh = () => {
    dispatch(
      fetchIntegrations({
        page: currentPage,
        page_size: pageSize,
        status: listFilterStatus,
      })
    );
    // toast.info('Refreshing data...');
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages()) {
      setClientPage(page);
    }
  };

  const getStatus = (status) => {
    if (status === "Running") {
      return (
        <span className="inline-flex items-center gap-x-1.5 min-w-[5.5rem] justify-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700 border border-green-200 shadow-sm transition-all duration-200">
          {status}
        </span>
      );
    } else if (status === "Failed") {
      return (
        <span className="inline-flex items-center gap-x-1.5 min-w-[5.5rem] justify-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700 border border-red-200 shadow-sm">
          {status}
        </span>
      );
    } else if (status === "Stopped") {
      return (
        <span className="inline-flex items-center gap-x-1.5 min-w-[5.5rem] justify-center rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-bold text-yellow-700 border border-yellow-200 shadow-sm">
          {status}
        </span>
      );
    } else if (status === "Draft") {
      return (
        <span className="inline-flex items-center gap-x-1.5 min-w-[5.5rem] justify-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700 border border-blue-200 shadow-sm">
          {status}
        </span>
      );
    }
  };

  // eslint-disable-next-line no-unused-vars
  const gotoAddItem = () => {
    var newGuid = generateGuid();
    navigate(`/integrations/${newGuid}/overview`); // Navigate to the next page
  };

  const editItem = (itemId) => {
    navigate(`/integrations/${itemId}/overview`); // Navigate to the next page
  };

  const deleteItem = async (itemId) => {
    setSelectedItemeToDelete(itemId);
  };

  const onConfirmCancel = () => {
    setSelectedItemeToDelete(null);
  };

  const onConfirm = async () => {
    try {
      // Dispatch the deleteIntegrationAsync thunk
      const resultAction = await dispatch(
        deleteIntegrationAsync(selectedItemToDelete)
      );

      // Check if the action was successful
      if (deleteIntegrationAsync.fulfilled.match(resultAction)) {
        dispatch(
          fetchIntegrations({
            page: currentPage,
            page_size: pageSize,
            status: listFilterStatus,
          })
        );

        toast.success("Integration deleted successfully!");
      } else {
        toast.error(resultAction.payload?.message || "Failed to delete integration.");
      }
    } catch (err) {
      toast.error(parseError(err));
    } finally {
      setSelectedItemeToDelete(null); // Close the modal
    }
  };

  const onPipelineDeleteConfirm = async () => {
    try {
      await deletePipeline(selectedPipelineToDelete);
      toast.success("Integration deleted successfully!");
      dispatch(fetchIntegrations({ page: currentPage, page_size: pageSize, status: listFilterStatus }));
    } catch (err) {
      toast.error(parseError(err));
    } finally {
      setSelectedPipelineToDelete(null);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const StatusIcon = ({ status }) => {
    switch (status) {
      case "Success":
        return (
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></g>
            <g id="SVGRepo_iconCarrier">
              {" "}
              <path
                d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z"
                fill="#15e411"
              ></path>{" "}
            </g>
          </svg>
        );
      case "Failed":
        return (
          <svg
            className="h-4 w-4"
            viewBox="0 0 32 32"
            version="1.1"
            fill="#000000"
          >
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></g>
            <g id="SVGRepo_iconCarrier">
              {" "}
              <defs> </defs>{" "}
              <g
                id="Page-1"
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd"
              >
                {" "}
                <g
                  id="Icon-Set-Filled"
                  transform="translate(-570.000000, -1089.000000)"
                  fill="#f24545"
                >
                  {" "}
                  <path
                    d="M591.657,1109.24 C592.048,1109.63 592.048,1110.27 591.657,1110.66 C591.267,1111.05 590.633,1111.05 590.242,1110.66 L586.006,1106.42 L581.74,1110.69 C581.346,1111.08 580.708,1111.08 580.314,1110.69 C579.921,1110.29 579.921,1109.65 580.314,1109.26 L584.58,1104.99 L580.344,1100.76 C579.953,1100.37 579.953,1099.73 580.344,1099.34 C580.733,1098.95 581.367,1098.95 581.758,1099.34 L585.994,1103.58 L590.292,1099.28 C590.686,1098.89 591.323,1098.89 591.717,1099.28 C592.11,1099.68 592.11,1100.31 591.717,1100.71 L587.42,1105.01 L591.657,1109.24 L591.657,1109.24 Z M586,1089 C577.163,1089 570,1096.16 570,1105 C570,1113.84 577.163,1121 586,1121 C594.837,1121 602,1113.84 602,1105 C602,1096.16 594.837,1089 586,1089 L586,1089 Z"
                    id="cross-circle"
                  >
                    {" "}
                  </path>{" "}
                </g>{" "}
              </g>{" "}
            </g>
          </svg>
        );
      case "Running":
        return (
          <svg
            className="h-4 w-4"
            viewBox="0 0 32 32"
            version="1.1"
            fill="#000000"
          >
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></g>
            <g id="SVGRepo_iconCarrier">
              {" "}
              <defs> </defs>{" "}
              <g
                id="Page-1"
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd"
              >
                {" "}
                <g
                  id="Icon-Set-Filled"
                  transform="translate(-570.000000, -1089.000000)"
                  fill="#0000ff"
                >
                  {" "}
                  <path
                    d="M591.657,1109.24 C592.048,1109.63 592.048,1110.27 591.657,1110.66 C591.267,1111.05 590.633,1111.05 590.242,1110.66 L586.006,1106.42 L581.74,1110.69 C581.346,1111.08 580.708,1111.08 580.314,1110.69 C579.921,1110.29 579.921,1109.65 580.314,1109.26 L584.58,1104.99 L580.344,1100.76 C579.953,1100.37 579.953,1099.73 580.344,1099.34 C580.733,1098.95 581.367,1098.95 581.758,1099.34 L585.994,1103.58 L590.292,1099.28 C590.686,1098.89 591.323,1098.89 591.717,1099.28 C592.11,1099.68 592.11,1100.31 591.717,1100.71 L587.42,1105.01 L591.657,1109.24 L591.657,1109.24 Z M586,1089 C577.163,1089 570,1096.16 570,1105 C570,1113.84 577.163,1121 586,1121 C594.837,1121 602,1113.84 602,1105 C602,1096.16 594.837,1089 586,1089 L586,1089 Z"
                    id="cross-circle"
                  >
                    {" "}
                  </path>{" "}
                </g>{" "}
              </g>{" "}
            </g>
          </svg>
        );
      default:
        return <span>&nbsp;</span>;
    }
  };

  // const getlastrunStatus = (status) => {
  //   switch (status) {
  //     case "Failed":
  //       return <span className="rounded-md bg-red-500 p-1 text-white">{status}</span>
  //       break;
  //     case "Success":
  //       return <span className="rounded-md bg-green-500 p-1 text-white">{status}</span>
  //       break;
  //     case "Running":
  //       return <span className="rounded-md bg-blue-500 p-1 text-white">{status}</span>
  //       break;
  //     default:
  //       return <span className="rounded-md bg-gray-500 p-1 text-white">{status}</span>
  //       break;
  //   }
  // }

  const showItemDetails = (itemId) => {
    navigate(`/integrations/${itemId}/details`); // Navigate to the next page
  };

  const showInvocations = (itemId) => {
    navigate(`/integrations/${itemId}/invocations`); // Navigate to the next page
  };

  const SkeletonRow = () => {
    return (
      <>
        {[...Array(pageSize)].map((_, index) => (
          <tr key={index}>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-52 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-28 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-28 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-24 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-40 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-60 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-20 bg-gray-200 rounded-md"></div>
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  };

  if (error) {
    return <div className="mt-24">Error: {error.message}</div>;
  }

  return (
    <>
      <div
        className="mt-2 h-full min-h-0 overflow-hidden bg-white px-4 py-6"
        id="listing"
      >
        <section className="">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-x-3 ml-3">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent uppercase tracking-wide">
                  Integrations{" "}
                  <span className="bg-blue-100 text-blue-700 rounded-full px-2 text-sm">
                    {status === "loading" ? (
                      <span className="animate-pulse">
                        <span className="h-6 w-10 rounded-full bg-blue-200"></span>
                      </span>
                    ) : (
                      <span>
                        {displayRows.length}
                      </span>
                    )}
                  </span>
                </h2>
              </div>
            </div>
            {/* Launch buttons removed from here */}
          </div>

          <div className="mt-2 md:flex md:items-center md:justify-between gap-4">
            <div className="flex-1 min-w-0 overflow-hidden">
              <ScrollContainer className="py-2">
                <div className="flex items-center gap-2 px-2 pr-4">
                  <button
                    onClick={() => navigate(-1)}
                    className="group flex items-center justify-center gap-2 rounded-full bg-blue-600 text-white px-5 py-2 text-sm font-bold transition-all duration-200 hover:bg-blue-700 shadow-sm active:scale-95"
                  >
                    <ArrowLeft size={18} strokeWidth={2.5} />
                    Back
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setListFilterStatus("All")}
                      className={`px-5 py-2 text-xs font-bold transition-all duration-200 sm:text-sm rounded-full border whitespace-nowrap
                        ${listFilterStatus === "All"
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-[#f0f7ff] text-[#1e3a8a] border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                        }`}
                    >
                      View all
                    </button>

                    <button
                      onClick={() => setListFilterStatus("Draft")}
                      className={`px-5 py-2 text-xs font-bold transition-all duration-200 sm:text-sm rounded-full border whitespace-nowrap
                        ${listFilterStatus === "Draft"
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-[#f0f7ff] text-[#1e3a8a] border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                        }`}
                    >
                      Draft
                    </button>

                    <button
                      onClick={() => setListFilterStatus("Running")}
                      className={`px-5 py-2 text-xs font-bold transition-all duration-200 sm:text-sm rounded-full border whitespace-nowrap
                        ${listFilterStatus === "Running"
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-[#f0f7ff] text-[#1e3a8a] border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                        }`}
                    >
                      Running
                    </button>

                    <button
                      onClick={() => setListFilterStatus("Stopped")}
                      className={`px-5 py-2 text-xs font-bold transition-all duration-200 sm:text-sm rounded-full border whitespace-nowrap
                      ${listFilterStatus === "Stopped"
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-[#f0f7ff] text-[#1e3a8a] border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                        }`}
                    >
                      Stopped
                    </button>
                  </div>

                  <div className="ml-2">
                    <button
                      onClick={handleRefresh}
                      className="rounded-full bg-[#f0f7ff] px-4 py-2 hover:bg-[#e0efff] text-[#1e3a8a] border border-[#cce3ff] transition-all duration-200 text-sm font-bold shadow-sm flex items-center gap-2 active:scale-95 whitespace-nowrap"
                    >
                      <svg className="h-4 w-4 text-[#3b82f6]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                  </div>
                </div>
              </ScrollContainer>
            </div>

            <div className="relative mt-4 flex items-center gap-3 md:mt-0">
              <span className="absolute">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="mx-3 h-5 w-5 text-gray-400 dark:text-gray-600"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
              </span>

              <input
                type="text"
                placeholder="Search Integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white block w-full rounded-lg border border-blue-400 py-2.5 pl-11 pr-5 text-gray-900 shadow-sm placeholder-gray-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 md:w-80 transition-all font-medium"
              />
              {userInfo?.role?.toLowerCase() !== "user" && (
                <button
                  onClick={() => navigate("/launch-builder")}
                  className="flex items-center gap-2 rounded-full bg-blue-600 text-white px-5 py-2.5 text-sm font-bold transition-all duration-200 hover:bg-blue-700 shadow-sm whitespace-nowrap active:scale-95"
                >
                  Add New
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-col">
            <ScrollContainer className="rounded-xl shadow-sm">
              <div className="inline-block min-w-full align-middle">
                <div className="relative overflow-visible">
                  <div className="overflow-hidden rounded-xl border border-blue-400 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th
                            scope="col"
                            className="px-2.5 py-4 text-left text-sm font-bold text-gray-900 tracking-wider"
                          >
                            <button className="flex items-center gap-x-2 focus:outline-none">
                              <span>Name</span>
                            </button>
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-4 text-left text-sm font-bold text-gray-900 tracking-wider"
                          >
                            Status
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-4 text-left text-sm font-bold text-gray-900 tracking-wider hide-on-mobile"
                          >
                            Created By
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-4 text-left text-sm font-bold text-gray-900 tracking-wider"
                          >
                            Created At
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-4 text-left text-sm font-bold text-gray-900 tracking-wider hide-on-mobile"
                          >
                            Last Executed
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-4 text-left text-sm font-bold text-gray-900 tracking-wider hide-on-mobile"
                          >
                            Description
                          </th>
                          {userInfo?.role?.toLowerCase() !== "user" && (
                            <th
                              scope="col"
                              className="px-2.5 py-4 text-left text-sm font-bold text-gray-900 tracking-wider"
                            >
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 border-t border-gray-200">
                        {status === "loading" && <SkeletonRow />}
                        {status === "succeeded" && pagedRows.map((row) => {
                          if (row.type === "pipeline") {
                            const isFanOut = row.pipeline_type === "fan_out";
                            const rowBorder = isFanOut ? "border-l-4 border-l-sky-400" : "border-l-4 border-l-violet-400";
                            return (
                              <tr key={row.key} className={`hover:bg-gray-50 transition-colors ${rowBorder}`}>
                                <td className="whitespace-nowrap px-2.5 py-1.5 text-sm font-medium">
                                  <button
                                    onClick={() => navigate(row.pipeline_type === "sequential" ? `/integrations/sequential/${row.pipelineId}` : `/integrations/pipeline/${row.pipelineId}`)}
                                    className="rounded-lg px-1 py-1 text-gray-900 hover:bg-gray-100 transition-colors text-left"
                                  >
                                    <span className="font-bold text-gray-900 block">{row.name}</span>
                                  </button>
                                </td>
                                <td className="whitespace-nowrap px-2.5 py-1 text-sm">{getStatus(row.status)}</td>
                                <td className="whitespace-nowrap px-2.5 py-1 text-sm text-black hide-on-mobile">{row.createdBy || "N/A"}</td>
                                <td className="whitespace-nowrap px-2.5 py-1 text-sm text-black">
                                  {formatDate(row.createdAt)}
                                </td>
                                <td className="whitespace-nowrap px-2.5 py-1 text-sm hide-on-mobile">
                                  {row.lastExecutedAt ? (
                                    <span className="text-black">{formatDate(row.lastExecutedAt)}</span>
                                  ) : (
                                    <span className="rounded bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-900 uppercase">Never</span>
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-2.5 py-1 text-sm text-black hide-on-mobile" title={row.description}>{row.description?.length > 30 ? row.description.slice(0, 30) + '…' : row.description}</td>
                                {userInfo?.role?.toLowerCase() !== "user" && (
                                  <td className="whitespace-nowrap px-2.5 py-1 text-sm">
                                    <div className="inline-flex items-center gap-1">
                                      <div className="group relative inline-block">
                                        <button
                                          onClick={() => navigate(row.pipeline_type === "sequential" ? `/integrations/sequential/${row.pipelineId}` : `/integrations/pipeline/${row.pipelineId}`)}
                                          className="rounded-lg px-1 py-1 text-blue-600 hover:bg-blue-100 transition-colors"
                                          aria-label="Edit integration"
                                        >
                                          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none"><path d="M21.2799 6.40005L11.7399 15.94C10.7899 16.89 7.96987 17.33 7.33987 16.7C6.70987 16.07 7.13987 13.25 8.08987 12.3L17.6399 2.75002C17.8754 2.49308 18.1605 2.28654 18.4781 2.14284C18.7956 1.99914 19.139 1.92124 19.4875 1.9139C19.8359 1.90657 20.1823 1.96991 20.5056 2.10012C20.8289 2.23033 21.1225 2.42473 21.3686 2.67153C21.6147 2.91833 21.8083 3.21243 21.9376 3.53609C22.0669 3.85976 22.1294 4.20626 22.1211 4.55471C22.1128 4.90316 22.0339 5.24635 21.8894 5.5635C21.7448 5.88065 21.5375 6.16524 21.2799 6.40005V6.00005Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path><path d="M11 4H6C4.93913 4 3.92178 4.42142 3.17163 5.17157C2.42149 5.92172 2 6.93913 2 8V18C2 19.0609 2.42149 20.0783 3.17163 20.8284C3.92178 21.5786 4.93913 22 6 22H17C19.21 22 20 20.2 20 18V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                        </button>
                                        <span className="pointer-events-none absolute -top-10 right-0 z-30 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                          Edit integration
                                        </span>
                                      </div>
                                      <div className="group relative inline-block">
                                        <button
                                          onClick={() => setSelectedPipelineToDelete(row.pipelineId)}
                                          className="rounded-lg px-1 py-1 text-blue-600 hover:bg-blue-100 transition-colors"
                                          aria-label="Delete integration"
                                        >
                                          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none"><path d="M10 11V17" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M14 11V17" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M6 7H20" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M6 7H12H18V18C18 19.6569 16.6569 21 15 21H9C7.34315 21 6 19.6569 6 18V7Z" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                        </button>
                                        <span className="pointer-events-none absolute -top-10 right-0 z-30 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                          Delete integration
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          }
                          // Standalone integration row
                          const item = row.item;
                          return (
                            <tr key={row.key} className="hover:bg-gray-50 transition-colors">
                              <td className="whitespace-nowrap px-2.5 py-1.5 text-sm font-medium">
                                <button onClick={() => showItemDetails(item.integration_id)}
                                  className="rounded-lg px-1 py-1 text-gray-900 hover:bg-gray-100 transition-colors text-left">
                                  <span className="font-bold text-gray-900 block">{item.name}</span>
                                </button>
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-1 text-sm">{getStatus(item.status)}</td>
                              <td className="whitespace-nowrap px-2.5 py-1 text-sm text-black hide-on-mobile">{item.createdBy || 'N/A'}</td>
                              <td className="whitespace-nowrap px-2.5 py-1 text-sm text-black">
                                {formatDate(item.createdAt)}
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-1 text-sm hide-on-mobile">
                                {item.lastExecutedAt === null ? (
                                  <span className="rounded bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-900 uppercase">Never</span>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-black">{formatDate(item.lastExecutedAt)}</span>
                                    <div className="group relative inline-block">
                                      <button onClick={() => showInvocations(item.integration_id)}
                                        aria-label="Run history"
                                        className="bg-blue-100 text-blue-700 rounded-lg p-1 hover:bg-blue-200 transition-colors">
                                        <svg className="h-6 w-6" viewBox="0 0 16 16" fill="none"><g id="SVGRepo_iconCarrier"><g fill="#000000"><path d="M5.314 1.256a.75.75 0 01-.07 1.058L3.889 3.5l1.355 1.186a.75.75 0 11-.988 1.128l-2-1.75a.75.75 0 010-1.128l2-1.75a.75.75 0 011.058.07zM7.186 1.256a.75.75 0 00.07 1.058L8.611 3.5 7.256 4.686a.75.75 0 10.988 1.128l2-1.75a.75.75 0 000-1.128l-2-1.75a.75.75 0 00-1.058.07zM2.75 7.5a.75.75 0 000 1.5h10.5a.75.75 0 000-1.5H2.75zM2 11.25a.75.75 0 01.75-.75h10.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75zM2.75 13.5a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z"></path></g></g></svg>
                                      </button>
                                      <span className="pointer-events-none absolute -top-10 right-0 z-30 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                        Run history
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-1 text-sm text-black hide-on-mobile" title={item.description}>{item.description?.length > 30 ? item.description.slice(0, 30) + '…' : item.description}</td>
                              {userInfo?.role?.toLowerCase() !== "user" && (
                                <td className="whitespace-nowrap px-2.5 py-2 text-sm">
                                  <div className="inline-flex items-center gap-1">
                                    <div className="group relative inline-block">
                                      <button onClick={() => editItem(item.integration_id)}
                                        aria-label="Edit integration"
                                        className="rounded-lg px-1 py-1 text-blue-600 hover:bg-blue-100 transition-colors">
                                        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none"><path d="M21.2799 6.40005L11.7399 15.94C10.7899 16.89 7.96987 17.33 7.33987 16.7C6.70987 16.07 7.13987 13.25 8.08987 12.3L17.6399 2.75002C17.8754 2.49308 18.1605 2.28654 18.4781 2.14284C18.7956 1.99914 19.139 1.92124 19.4875 1.9139C19.8359 1.90657 20.1823 1.96991 20.5056 2.10012C20.8289 2.23033 21.1225 2.42473 21.3686 2.67153C21.6147 2.91833 21.8083 3.21243 21.9376 3.53609C22.0669 3.85976 22.1294 4.20626 22.1211 4.55471C22.1128 4.90316 22.0339 5.24635 21.8894 5.5635C21.7448 5.88065 21.5375 6.16524 21.2799 6.40005V6.00005Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path><path d="M11 4H6C4.93913 4 3.92178 4.42142 3.17163 5.17157C2.42149 5.92172 2 6.93913 2 8V18C2 19.0609 2.42149 20.0783 3.17163 20.8284C3.92178 21.5786 4.93913 22 6 22H17C19.21 22 20 20.2 20 18V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                      </button>
                                      <span className="pointer-events-none absolute -top-10 right-0 z-30 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                        Edit integration
                                      </span>
                                    </div>
                                    <div className="group relative inline-block">
                                      <button onClick={() => deleteItem(item.integration_id)}
                                        aria-label="Delete integration"
                                        className="rounded-lg px-1 py-1 text-blue-600 hover:bg-blue-100 transition-colors">
                                        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none"><path d="M10 11V17" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M14 11V17" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M6 7H20" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M6 7H12H18V18C18 19.6569 16.6569 21 15 21H9C7.34315 21 6 19.6569 6 18V7Z" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                      </button>
                                      <span className="pointer-events-none absolute -top-10 right-0 z-30 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                        Delete integration
                                      </span>
                                    </div>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                        {displayRows.length === 0 && status === "succeeded" && (
                          <tr>
                            <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-400 font-medium">
                              No integrations available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </ScrollContainer>

            {filteredIntegrations.length > 0 && (
              <div className="mt-10 flex items-center justify-center px-4">
                {status === "loading" ? (
                  <div className="my-1 animate-pulse">
                    <div className="h-12 w-full max-w-md bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  </div>
                ) : (
                  <div className="flex select-none items-center gap-x-4">
                    <button
                      onClick={() => handlePageChange(clientPage - 1)}
                      disabled={clientPage === 1}
                      className={`flex h-10 min-w-[120px] items-center justify-center gap-1 px-4 shrink-0 transition-all duration-300 border rounded-full text-xs sm:text-sm font-bold shadow-sm active:scale-95 ${clientPage === 1
                        ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                        : "bg-[#f0f7ff] text-[#1e3a8a] border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff] hover:shadow-md"
                        }`}
                    >
                      Previous
                    </button>

                    <div className="flex items-center gap-2">
                      {Array.from(
                        { length: totalPages() },
                        (_, index) => index + 1
                      ).map((page) => (
                        <button
                          key={page}
                          onClick={() =>
                            clientPage !== page
                              ? handlePageChange(page)
                              : undefined
                          }
                          className={`w-10 h-10 shrink-0 flex items-center justify-center transition-all duration-300 border rounded-full text-sm font-bold ${clientPage === page
                            ? "bg-blue-600 text-white border-blue-600 shadow-md transform scale-110 z-10"
                            : "bg-[#f0f7ff] text-[#1e3a8a] border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                            }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => handlePageChange(clientPage + 1)}
                      disabled={clientPage === totalPages()}
                      className={`flex h-10 min-w-[120px] items-center justify-center gap-1 px-4 shrink-0 transition-all duration-300 border rounded-full text-xs sm:text-sm font-bold shadow-sm active:scale-95 ${clientPage === totalPages()
                        ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                        : "bg-[#f0f7ff] text-[#1e3a8a] border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff] hover:shadow-md"
                        }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
      <ModalConfirm
        isOpen={selectedItemToDelete !== null}
        onCancel={onConfirmCancel}
        onConfirm={onConfirm}
        title={"Delete Integration"}
      >
        <div>Are you sure you want to delete this integration?</div>
      </ModalConfirm>
      <ModalConfirm
        isOpen={selectedPipelineToDelete !== null}
        onCancel={() => setSelectedPipelineToDelete(null)}
        onConfirm={onPipelineDeleteConfirm}
        title={"Delete Integration"}
      >
        <div>Are you sure you want to delete this integration?</div>
      </ModalConfirm>
    </>
  );
};

export default Listing;
